import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle, User, LogOut, LayoutDashboard, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, SHOP } from "@/lib/api";

function NotificationBell() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/notifications").then((r) => setNotes(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);

  if (!user) return null;
  const unread = notes.filter((n) => !n.read).length;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await api.post("/notifications/read-all").catch(() => {});
      setNotes((ns) => ns.map((n) => ({ ...n, read: true })));
    }
  };

  return (
    <div className="relative">
      <button onClick={toggle} aria-label="Notifications" data-testid="notifications-btn"
        className="relative border-2 border-black bg-white p-2 shadow-hard-sm brutal-hover">
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -top-2 -right-2 bg-accent border-2 border-black text-[10px] font-black w-5 h-5 flex items-center justify-center" data-testid="notifications-badge">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white border-2 border-black shadow-hard z-50" data-testid="notifications-panel">
          <p className="eyebrow px-4 py-3 border-b-2 border-black">Notifications</p>
          {notes.length === 0 ? (
            <p className="px-4 py-4 text-sm text-neutral-500" data-testid="notifications-empty">No updates yet — repair status changes will appear here.</p>
          ) : (
            <ul className="max-h-64 overflow-y-auto divide-y-2 divide-neutral-100">
              {notes.map((n) => (
                <li key={n._id} className="px-4 py-3 text-sm" data-testid={`notification-${n._id}`}>
                  <span className="font-display font-bold uppercase">{n.reference}</span> — your {n.device} is now <strong className="uppercase">{n.status}</strong>.
                  {n.status === "ready" && <span className="block text-accent font-bold mt-1">Ready to collect during opening hours (Mon–Fri, 10:30am–6pm).</span>}
                  <span className="block text-xs text-neutral-400 mt-1">{new Date(n.created_at).toLocaleString("en-GB")}</span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/dashboard" onClick={() => setOpen(false)} className="block px-4 py-3 border-t-2 border-black font-display text-xs font-bold uppercase tracking-wider hover:bg-neutral-50" data-testid="notifications-dashboard-link">
            View my account
          </Link>
        </div>
      )}
    </div>
  );
}

const NAV = [
  { to: "/", label: "Home" },
  { to: "/book", label: "Book a Repair" },
  { to: "/sell", label: "Sell My Device" },
  { to: "/buy", label: "Buy a Device" },
  { to: "/about", label: "About Us" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const linkCls = ({ isActive }) =>
    `font-display text-sm font-semibold uppercase tracking-wider px-1 py-2 border-b-2 transition-colors ${
      isActive ? "border-black text-black" : "border-transparent text-neutral-500 hover:text-black"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-white border-b-2 border-black" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <img src="/logo.webp" alt="Appleman Tech Repair logo" className="h-12 w-12 object-contain" data-testid="header-logo" />
          <span className="font-display font-black text-lg leading-none uppercase tracking-tight">
            Appleman<br />
            <span className="text-xs font-bold tracking-[0.3em] text-neutral-500">Tech Repair</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="header-nav">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {n.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" className={linkCls} data-testid="nav-admin">Admin</NavLink>
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <NotificationBell />
          <a
            href={SHOP.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
            className="flex items-center gap-2 border-2 border-black bg-white px-4 py-2 font-display text-sm font-bold uppercase tracking-wider shadow-hard-sm brutal-hover"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          {user ? (
            <>
              <Link
                to="/dashboard"
                data-testid="header-dashboard-btn"
                className="flex items-center gap-2 border-2 border-black bg-white px-4 py-2 font-display text-sm font-bold uppercase tracking-wider shadow-hard-sm brutal-hover"
              >
                <LayoutDashboard className="h-4 w-4" /> My Account
              </Link>
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid="header-logout-btn"
                className="flex items-center gap-2 border-2 border-black bg-black text-white px-4 py-2 font-display text-sm font-bold uppercase tracking-wider shadow-hard-sm brutal-hover"
              >
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </>
          ) : (
            <Link
              to="/book"
              data-testid="header-book-btn"
              className="flex items-center gap-2 border-2 border-black bg-accent px-4 py-2 font-display text-sm font-bold uppercase tracking-wider shadow-hard-sm brutal-hover"
            >
              <User className="h-4 w-4" /> Book a Repair
            </Link>
          )}
        </div>

        <div className="flex lg:hidden items-center gap-3">
          <NotificationBell />
          <button className="border-2 border-black p-2 shadow-hard-sm" onClick={() => setOpen(!open)} data-testid="header-mobile-menu-btn" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t-2 border-black bg-white px-4 py-6 flex flex-col gap-4" data-testid="header-mobile-menu">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className={linkCls} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {n.label}
            </NavLink>
          ))}
          <a href={SHOP.whatsapp} target="_blank" rel="noopener noreferrer" className="font-display text-sm font-bold uppercase tracking-wider text-neutral-600" data-testid="mobile-whatsapp-link">
            WhatsApp us
          </a>
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="font-display text-sm font-bold uppercase tracking-wider" data-testid="mobile-dashboard-link">My Account</Link>
              <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="text-left font-display text-sm font-bold uppercase tracking-wider" data-testid="mobile-logout-btn">Logout</button>
            </>
          ) : (
            <Link to="/book" onClick={() => setOpen(false)} className="font-display text-sm font-bold uppercase tracking-wider" data-testid="mobile-book-link">Book a Repair</Link>
          )}
        </div>
      )}
    </header>
  );
}
